import { ChevronDown } from "lucide-react";
import { forwardRef, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * O par do `Input` para escolha em lista — mesma altura, mesma borda, mesmo
 * anel de foco, para que um formulário misto não pareça montado de peças de
 * dois sistemas.
 *
 * Existia escrito à mão nos dois campos de Meu perfil, com a mesma lista de
 * classes copiada do `Input` — e já sem o `disabled:` num deles.
 *
 * **A seta é desenhada, não a nativa.** A do navegador se posiciona sozinha,
 * encostada na borda: o `px-3` do campo alcança o texto e não alcança ela, e o
 * resultado era um texto a 12px da borda esquerda com uma seta a ~4px da
 * direita. Com `appearance-none` e um `ChevronDown` em `right-3`, os dois lados
 * ficam nos mesmos 12px do `Input`, e a cor sai de token em vez de ser a do
 * sistema operacional. É o mesmo tratamento que o seletor do `LocationFinder`
 * já usava — agora os dois combinam.
 *
 * O Figma (nó `657:686`) já especificava exatamente isso: seta de 16px a 12px
 * da borda, em `fg/muted`. Quem estava fora do desenho era o código, que deixava
 * a posição para o navegador decidir.
 *
 * O `className` cai no **invólucro**, não no `<select>`: é lá que a margem e a
 * largura que as telas passam precisam morar, e a seta se posiciona por ele.
 * Classe que altere o campo em si (tamanho de texto, altura) não passa por
 * aqui — se precisar, o componente ganha prop.
 *
 * Não cobre o seletor de município do `LocationFinder`, que é maior (h-11) e
 * serve de controle de destaque numa página de serviço, não de campo de
 * formulário.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className, ...props }, ref) => (
  // `has-[select:disabled]` esmaece o conjunto: com a opacidade no `<select>`
  // a seta ficava opaca sobre um campo apagado, porque ela é irmã dele e não
  // filha. Os mesmos 50% do `Input` — o par prometido no topo se desfaz se cada
  // um apagar numa intensidade diferente.
  <span
    className={cn(
      "relative block has-[select:disabled]:cursor-not-allowed has-[select:disabled]:opacity-50",
      className,
    )}
    data-slot="select"
  >
    <select
      ref={ref}
      className={cn(
        "flex h-10 w-full appearance-none rounded-md border border-input-border bg-input-bg pr-10 pl-3 text-body-md text-input-fg",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed",
        "aria-[invalid=true]:border-input-error aria-[invalid=true]:ring-input-error",
      )}
      {...props}
    />
    <ChevronDown
      aria-hidden
      className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-fg-muted"
    />
  </span>
));
Select.displayName = "Select";
